import { useCallback, useContext, useRef, useEffect } from 'react'
import { AppContext } from '../context/AppProvider'
import {
  addToFavorites, removeFromFavorites, favoritesFailedFetch,
  favoritesSuccessFetch, favoritesLodingFetch, favoritesReset
} from '../actions/favoritesActions'
import { jwtDecode } from 'jwt-decode'
import { buildPublicApiUrl } from '../config/api'

export const useFavorites = () => {
  const { state, dispatch } = useContext(AppContext)
  const apiUrl = buildPublicApiUrl('favorites')

  const stateRef = useRef(state)
  useEffect(() => {
    stateRef.current = state
  }, [state])

  const getFavorites = useCallback(async (page) => {
    const token = localStorage.getItem('token')
    let headers = { 'Content-Type': 'application/json' }
    if (token) {
      headers = { ...headers, Authorization: `${token}` }
      dispatch(favoritesLodingFetch())
      try {
        const userData = jwtDecode(token)
        let url = `${apiUrl}/${userData.favorites_id}`
        if (page) {
          url += `?page=${page}`
        }
        const response = await fetch(`${url}`, {
          headers: { ...headers, Authorization: `${token}` }
        })
        const data = await response.json()
        dispatch(favoritesSuccessFetch(data))
      } catch (error) {
        dispatch(favoritesFailedFetch(error))
      }
    }
  }, [dispatch])

  const addItemToFavorites = useCallback(async (currentItemId) => {
    const token = localStorage.getItem('token')
    let headers = { 'Content-Type': 'application/json' }
    dispatch({ type: 'ATTEMPT_NAVIGATE_PROTECTED_ROUTE' })
    if (token) {
      const item = currentItemId.current ? currentItemId.current : currentItemId.data
      const favoritesId = state.user.data.favorites
      const favoritesItemIds = state.favorites.results?.map((item) => item._id)
      headers = { ...headers, Authorization: `${token}` }
      try {
        const response = await fetch(
          `${apiUrl}/${favoritesId}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ ids: [...favoritesItemIds, item._id] })
          }
        )
        const { message } = await response.json()
        if (message !== 'Unauthorized') {
          dispatch(addToFavorites(item))
        }
      } catch (error) {
        console.error('Failed to fetch filters:', error)
      }
    }
  }, [dispatch, state.user.data.favorites, state.favorites.results])

  const removeItemFromFavorites = useCallback(async (currentItemId) => {
    const token = localStorage.getItem('token')
    let headers = { 'Content-Type': 'application/json' }
    dispatch({ type: 'ATTEMPT_NAVIGATE_PROTECTED_ROUTE' })
    if (token) {
      const itemId = currentItemId.current ? currentItemId.current._id : currentItemId.data._id
      const favoritesId = state.user.data.favorites
      const favoritesItem = state.favorites.results?.filter(item => item._id !== itemId)
      const favoritesItemIds = favoritesItem.map(item => item._id)
      headers = { ...headers, Authorization: `${token}` }
      try {
        const response = await fetch(
          `${apiUrl}/${favoritesId}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ ids: favoritesItemIds })
          }
        )
        const { message } = await response.json()
        if (message !== 'Unauthorized') {
          dispatch(removeFromFavorites(favoritesItem))
        }
        return { data: message, error: null }
      } catch (error) {
        console.error('Failed to fetch filters:', error)
      }
    }
  }, [dispatch, state.user.data.favorites, state.favorites.results])

  const requestFavoritesOffer = useCallback(async () => {
    const token = localStorage.getItem('token')
    let headers = { 'Content-Type': 'application/json' }
    dispatch({ type: 'ATTEMPT_NAVIGATE_PROTECTED_ROUTE' })
    if (!token) {
      return { message: 'Unauthorized', statusCode: 401, error: 'Unauthorized' }
    }

    headers = { ...headers, Authorization: `${token}` }
    try {
      const favoritesId = state.user.data.favorites
      const response = await fetch(`${apiUrl}/${favoritesId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({})
      })
      const payload = await response.json()
      return {
        message: payload.message,
        statusCode: payload.statusCode,
        error: payload.error ?? null
      }
    } catch (error) {
      console.error('Failed to request favorites offer:', error)
      return { message: 'A apărut o eroare la trimiterea cererii.', statusCode: 500, error }
    }
  }, [dispatch, state.user.data.favorites])

  const resetFavoritesState = useCallback(async () => {
    dispatch(favoritesReset())
  }, [dispatch])

  return { addItemToFavorites, removeItemFromFavorites, getFavorites, resetFavoritesState, requestFavoritesOffer }
}
